import React, { useEffect, useState } from 'react';
import { APITypes } from '../../../primary-record-types';
import { TabView } from '../../v2BaseComponents/TabView';
import { getIntl } from '../../../utils/intl';
import CreateOrganizationForm from './CreateOrganizationForm';
import JoinWithNoPendingInvitations from './JoinWithNoPendingInvitations';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import useMyOrganizationInvitations from '../../../hooks/api/useMyOrganizationInvitations';
import OrganizationPendingInvitation from './OrganizationPendingInvitation';
import { useCapturedUrlUserType } from '../../../hooks/useUrlAppParams';

const intl = getIntl('ProfessionalNoOrganizationGate', {
    create: 'Create New Organization',
    join: 'Join Existing Organization',
});

export type ProfessionalNoOrganizationGateOwnProps = {
    isLoading: boolean;
    onCreateOrganization: (organization: APITypes.organizationService.CreateOrganizationRequest) => void;
};

export type ProfessionalNoOrganizationGateProps = ProfessionalNoOrganizationGateOwnProps;

type ActiveTab = 'create' | 'join';

const ProfessionalNoOrganizationGate: React.FC<ProfessionalNoOrganizationGateProps> = ({
    isLoading,
    onCreateOrganization,
}) => {
    const urlUserType = useCapturedUrlUserType();
    const { isLoadingMyOrganizationInvitation, myOrganizationInvitation } = useMyOrganizationInvitations();
    const [selectedTab, setSelectedTab] = useState<ActiveTab | null>(null);
    // default the tab to create if this is likely a professional user setting up their organization
    useEffect(() => {
        if (selectedTab !== null) return;

        if (isLoadingMyOrganizationInvitation) return;

        // potential organization owners will have the special link specifying they are a professional user.  Subsequent agents will not have this link
        if (urlUserType === 'professional' && !myOrganizationInvitation) {
            setSelectedTab('create');
        } else {
            setSelectedTab('join');
        }
    }, [urlUserType, isLoadingMyOrganizationInvitation, myOrganizationInvitation, selectedTab]);

    return (
        <AnimateLoadedContent isLoading={isLoading || isLoadingMyOrganizationInvitation || !selectedTab}>
            {() => (
                <TabView
                    onChange={(val) => setSelectedTab(val as ActiveTab)}
                    selectedTab={selectedTab as ActiveTab}
                    tabs={[
                        {
                            value: 'create',
                            title: intl('create'),
                            renderTabContent: () => <CreateOrganizationForm onSubmit={onCreateOrganization} />,
                        },
                        {
                            value: 'join',
                            title: intl('join'),
                            renderTabContent: () =>
                                myOrganizationInvitation ? (
                                    <OrganizationPendingInvitation />
                                ) : (
                                    <JoinWithNoPendingInvitations />
                                ),
                        },
                    ]}
                />
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(ProfessionalNoOrganizationGate);
